import { Link, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock3, Phone, Mail } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { usePosts } from "../hooks/usePosts";
import { parseMd } from "../utils/md";
import { SITE, waLink } from "../data/site";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const renderInline = (text) =>
  text.split("**").map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const Table = ({ head, rows }) => (
  <div className="my-8 overflow-x-auto border border-slate-200" data-testid="blog-table">
    <table className="w-full min-w-[640px] border-collapse text-left">
      <thead>
        <tr className="bg-navy text-white">
          {head.map((h) => (
            <th key={h} className="px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={`${i % 2 ? "bg-slate-50" : "bg-white"}`}>
            {r.map((cell, j) => (
              <td key={j} className={`px-4 py-3 align-top text-sm ${j === 0 ? "font-display font-bold text-navy" : "text-slate-600"}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BlogPost = () => {
  const { slug } = useParams();
  const { data: posts } = usePosts();
  const post = posts.find((p) => p.slug === slug);
  const blocks = post ? parseMd(post.content) : [];

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 pt-20 text-center">
        <h1 className="font-display text-3xl font-extrabold text-navy">Article not found</h1>
        <Link to="/blog" data-testid="blog-back-link" className="mt-6 bg-safety px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-white">
          Back to Blog
        </Link>
      </div>
    );
  }

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <>
      <Seo
        title={`${post.title} | Universal Engineers`}
        description={post.seoDescription}
        keywords={post.keywords}
        path={`/blog/${post.slug}`}
        jsonld={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.seoDescription,
          datePublished: post.date,
          author: { "@type": "Organization", name: "Universal Engineers", url: SITE.domain },
          publisher: { "@type": "Organization", name: "Universal Engineers", url: SITE.domain },
          image: `${SITE.domain}${post.image}`,
          mainEntityOfPage: `${SITE.domain}/blog/${post.slug}`,
        }}
      />

      <section className="grain relative overflow-hidden bg-navy pb-16 pt-32 sm:pb-20 lg:pt-40" data-testid="blog-post-hero">
        <div className="bp-grid absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="blog-breadcrumb">
              <Link to="/" className="hover:text-safety">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-safety">Blog</Link>
            </nav>
            <h1 className="mt-7 font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-5 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
              <span className="flex items-center gap-2"><CalendarDays size={13} className="text-safety" /> {formatDate(post.date)}</span>
              <span className="flex items-center gap-2"><Clock3 size={13} className="text-safety" /> {post.readTime}</span>
              <span className="bg-safety px-2.5 py-1 text-white">{post.tag || "Article"}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <article className="py-14 sm:py-16" data-testid="blog-article">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {blocks.map((b, i) => {
            if (b.type === "h2")
              return (
                <h2 key={i} className="mt-12 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl" data-testid={`blog-h2-${i}`}>
                  {b.text}
                </h2>
              );
            if (b.type === "h3")
              return (
                <h3 key={i} className="mt-8 font-display text-xl font-bold tracking-tight text-navy" data-testid={`blog-h3-${i}`}>
                  {b.text}
                </h3>
              );
            if (b.type === "p")
              return (
                <p key={i} className="mt-5 text-[15px] leading-relaxed text-slate-700" data-testid={`blog-p-${i}`}>
                  {renderInline(b.text)}
                </p>
              );
            if (b.type === "list")
              return (
                <ul key={i} className="mt-5 space-y-3" data-testid={`blog-list-${i}`}>
                  {b.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-700">
                      <span className="mt-2 inline-block h-2 w-2 shrink-0 bg-safety" />
                      <span>{renderInline(item)}</span>
                    </li>
                  ))}
                </ul>
              );
            if (b.type === "table") return <Table key={i} head={b.head} rows={b.rows} />;
            return null;
          })}

          <div className="grain relative mt-14 overflow-hidden bg-navy p-8 sm:p-10" data-testid="blog-cta">
            <div className="bp-grid absolute inset-0" />
            <div className="relative z-10">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                Planning a solar, wind or infrastructure project?
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
                Get genuine 3M cable accessories with type-test documentation — quotes by
                next working day, first half.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/enquiry" data-testid="blog-cta-enquiry" className="group flex items-center gap-2.5 bg-safety px-6 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white hover:text-navy">
                  Send Enquiry <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="blog-cta-whatsapp" className="border border-slate-600 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]">
                  WhatsApp Us
                </a>
              </div>
              <div className="mt-6 flex flex-wrap gap-5 font-mono text-[11px] text-slate-400">
                <a href={SITE.phoneHref} className="flex items-center gap-2 hover:text-safety"><Phone size={12} /> {SITE.phoneDisplay}</a>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-safety"><Mail size={12} /> {SITE.email}</a>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-14 border-t border-slate-200 pt-10" data-testid="blog-related">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety">Continue reading</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`} className="group border border-slate-200 p-5 transition-all hover:border-safety">
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{formatDate(r.date)} · {r.readTime}</p>
                    <p className="mt-2 font-display text-base font-bold leading-snug text-navy group-hover:text-safety">{r.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
};

export default BlogPost;
